import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useSite } from '../../context/SiteContext';

const AuditRecords = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filteredRecords, setFilteredRecords] = useState([]);

  const statusOptions = ['All', 'Draft', 'Submitted', 'Reviewed', 'Approved', 'CAPA Outstanding', 'Closed'];

  // REL-18: audits are site-scoped; reload when the active site changes.
  const { activeSiteId } = useSite();

  useEffect(() => {
    fetchData();
  }, [activeSiteId]);

  useEffect(() => {
    if (filterStatus === 'All') {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(records.filter(r => r.workflow_status === filterStatus));
    }
  }, [filterStatus, records]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = activeSiteId ? { site_id: activeSiteId } : {};
      const [recordsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/audit-records`, { headers, params }),
        axios.get(`${API}/audit-schedules`, { headers, params })
      ]);
      
      setRecords(recordsRes.data);
      setSchedules(schedulesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Reviewed': 'bg-purple-100 text-purple-700',
      'Approved': 'bg-green-100 text-green-700',
      'CAPA Outstanding': 'bg-yellow-100 text-yellow-700',
      'Closed': 'bg-teal-100 text-teal-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Findings summary badges. Shared by the desktop table cell and the mobile
  // card so the two can't drift apart.
  const renderFindingsSummary = (record) => {
    const findings = record.findings || [];
    const evaluated = findings.filter(f => f.status);
    const failed = evaluated.filter(f => f.status === 'Fail');

    if (!evaluated.length) return <span className="text-gray-400">Pending</span>;
    if (!failed.length) return <span className="text-green-600">No Issues</span>;

    const critical = failed.filter(f => f.severity === 'Critical').length;
    const major = failed.filter(f => f.severity === 'Major').length;
    const moderate = failed.filter(f => f.severity === 'Moderate').length;
    const minor = failed.filter(f => f.severity === 'Minor').length;
    const unclassified = failed.length - critical - major - moderate - minor;

    return (
      <>
        {critical > 0 && (
          <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{critical} Critical</span>
        )}
        {major > 0 && (
          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded">{major} Major</span>
        )}
        {moderate > 0 && (
          <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">{moderate} Moderate</span>
        )}
        {minor > 0 && (
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">{minor} Minor</span>
        )}
        {unclassified > 0 && (
          <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{unclassified} Failed</span>
        )}
      </>
    );
  };

  const startAudit = async (schedule) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Create audit record from schedule
      const auditData = {
        audit_title: schedule.audit_title,
        // audit_type is deliberately NOT sent: the server resolves it from the
        // template, which is what defines the kind of audit. This used to send a
        // hardcoded 'Internal' — a label the template form does not even offer
        // ('Internal Audit' is the real one) — so every audit started from a
        // schedule was stamped with a phantom type and went missing from the
        // audits-by-type breakdown. This page does not load templates, so
        // guessing here would just move the guess.
        template_id: schedule.template_id,
        template_name: schedule.template_name || '',
        area: schedule.area || '',
        location: schedule.location || '',
        department: schedule.department || '',
        lead_auditor_id: schedule.auditor_id,
        lead_auditor_name: schedule.auditor_name,
        // Carry the team planned on the schedule onto the audit record
        co_auditors: (schedule.co_auditors || []).filter(c => c.user_id !== schedule.auditor_id),
        audit_date: schedule.scheduled_date,
        // Carry contractor attribution from the schedule onto the record, the
        // same way the co-auditor team is carried above. Without this the
        // contractor planned at scheduling would be lost the moment the audit
        // started, and the audit would never appear in their EHS history.
        contractor_company_id: schedule.contractor_company_id || undefined,
        // REL-18: the audit belongs to the schedule's site
        schedule_id: schedule.id,
        site_id: schedule.site_id || activeSiteId || undefined,
        findings: []
      };

      const response = await axios.post(`${API}/audit-records`, auditData, { headers });

      // Update schedule status so it no longer shows as "Scheduled"
      await axios.put(
        `${API}/audit-schedules/${schedule.id}`,
        { status: 'In Progress' },
        { headers }
      );

      // Remove from local schedules list
      setSchedules(prev => prev.filter(s => s.id !== schedule.id));

      navigate('/audits/conduct', { state: { schedule, auditRecordId: response.data.id } });
    } catch (err) {
      console.error('Failed to start audit:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audits Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-800">📝 Audit Records</h1>
          <p className="text-gray-600 mt-1">{filteredRecords.length} audit(s) found</p>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filterStatus === status
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Scheduled Audits - Pending Completion */}
        {schedules.filter(s => s.status === 'Scheduled').length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">🔔 Scheduled Audits - Start Audit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedules.filter(s => s.status === 'Scheduled').map(schedule => (
                <div key={schedule.id} className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
                  <h3 className="font-bold text-gray-800 mb-2">{schedule.audit_title}</h3>
                  <p className="text-sm text-gray-600 mb-1">Auditor: {schedule.auditor_name}</p>
                  <p className="text-sm text-gray-600 mb-1">Template: {schedule.template_name}</p>
                  <p className="text-sm text-gray-500 mb-3">
                    {formatDate(schedule.scheduled_date)} at {formatTime(schedule.scheduled_date)}
                  </p>
                  {schedule.location && (
                    <p className="text-xs text-gray-500 mb-3">📍 {schedule.location}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/audits/schedule', { state: { editSchedule: schedule } })}
                      className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => startAudit(schedule)}
                      className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Start
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Audits Records */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">📊 Audit Records</h2>
          {filteredRecords.length > 0 ? (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audit Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auditor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Compliance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Findings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecords.map(record => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-800">{record.audit_title}</div>
                        {record.location && (
                          <div className="text-xs text-gray-500">📍 {record.location}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{record.audit_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(record.audit_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {record.lead_auditor_name}
                        {record.co_auditors?.length > 0 && (
                          <span
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"
                            title={`Co-auditors: ${record.co_auditors.map(c => c.name).join(', ')}`}
                          >
                            +{record.co_auditors.length}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(record.workflow_status)}`}>
                          {record.workflow_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {record.compliance_score !== null ? (
                          <span className={`text-sm font-bold ${getComplianceColor(record.compliance_score)}`}>
                            {record.compliance_score.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs flex flex-wrap gap-1">
                          {renderFindingsSummary(record)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/audits/detail/${record.id}`)}
                            className="text-blue-600 hover:underline text-sm"
                          >
                            View Details
                          </button>
                          {record.workflow_status === 'Draft' && (
                            <button
                              onClick={() => navigate('/audits/conduct', { state: { auditRecordId: record.id } })}
                              className="text-purple-600 hover:underline text-sm"
                            >
                              Continue
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* Mobile card stack — same records, no horizontal scrolling */}
              <div className="md:hidden p-4 space-y-4">
                {filteredRecords.map(record => (
                  <div key={record.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <div className="text-base font-semibold text-gray-800">{record.audit_title}</div>
                      {record.location && (
                        <div className="text-xs text-gray-500">📍 {record.location}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(record.workflow_status)}`}>
                        {record.workflow_status}
                      </span>
                      {record.compliance_score !== null && (
                        <span className={`text-sm font-bold ${getComplianceColor(record.compliance_score)}`}>
                          {record.compliance_score.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 space-y-0.5">
                      <div><span className="font-semibold">Type:</span> {record.audit_type}</div>
                      <div><span className="font-semibold">Date:</span> {formatDate(record.audit_date)}</div>
                      <div>
                        <span className="font-semibold">Auditor:</span> {record.lead_auditor_name}
                        {record.co_auditors?.length > 0 && (
                          <span
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"
                            title={`Co-auditors: ${record.co_auditors.map(c => c.name).join(', ')}`}
                          >
                            +{record.co_auditors.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs flex flex-wrap gap-1">
                      {renderFindingsSummary(record)}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-1">
                      <button
                        onClick={() => navigate(`/audits/detail/${record.id}`)}
                        className="text-blue-600 hover:underline text-sm font-semibold"
                      >
                        View Details
                      </button>
                      {record.workflow_status === 'Draft' && (
                        <button
                          onClick={() => navigate('/audits/conduct', { state: { auditRecordId: record.id } })}
                          className="text-purple-600 hover:underline text-sm font-semibold"
                        >
                          Continue
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <p className="text-gray-600">No audit records found for the selected filter.</p>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
  );
};

export default AuditRecords;
